import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import ModalConfirmationContent from '../../../../shared/components/modal-confirmation-content/modal-confirmation-content';
import Modal from '../../../../shared/components/modal/modal';
import { useConfirmationError } from '../../../../shared/hooks/use-confirmation-error';
import { useAppSelector } from '../../../../store/hooks';
import { requestPreviewAlertId } from '../../../request/constants/constants';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useDetachSignRequestMutation } from '../../api/sign-requests-api';
import { signRequestAlertId, signRequestDetachModalId } from '../../constants/constants';
import { selectTempSignRequestId, setTempSignRequestId } from '../../store/sign-requests-slice';

const ScanRequestDetachModalContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['sign-requests', 'common']);
  const dispatch = useDispatch();
  const activeSignRequestId = useAppSelector(selectTempSignRequestId);
  const [detachSignRequest, { isLoading }] = useDetachSignRequestMutation();
  const { confirmationError, addConfirmationError, resetConfirmationError } = useConfirmationError();

  const closeFormModal = () => {
    dispatch(closeModal(signRequestDetachModalId));
    dispatch(setTempSignRequestId(null));
  };

  const setSignRequestDetachAlertMessage = (alertId: string) => {
    dispatch(
      setAlert({
        id: alertId,
        type: 'success',
        title: t('signRequestDetachModal.message.fileDetached'),
      }),
    );
  };

  const onModalSubmit = () => {
    if (!activeSignRequestId) {
      addConfirmationError(t('signScanRequest.validationError.detachFailed', { ns: 'common' }));
      return;
    }

    detachSignRequest(activeSignRequestId)
      .unwrap()
      .then(() => {
        closeFormModal();
        setSignRequestDetachAlertMessage(signRequestAlertId);
        setSignRequestDetachAlertMessage(requestPreviewAlertId);
      })
      .catch(() => addConfirmationError(t('signScanRequest.validationError.detachFailed', { ns: 'common' })));
  };

  useEffect(
    () => resetConfirmationError(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen],
  );

  return (
    <Modal
      title={t('signRequestDetachModal.title')}
      onCancel={closeFormModal}
      isLoading={isLoading}
      customSubmitText={t('signScanRequest.action.detach', { ns: 'common' })}
      onOk={onModalSubmit}
      isOpen={isOpen}
      afterClose={afterClose}
    >
      <ModalConfirmationContent
        confirmDeleteText={t('signRequestDetachModal.warning')}
        confirmationError={confirmationError}
      />
    </Modal>
  );
};

const SignRequestDetachModal = (): JSX.Element => {
  return <LazyModal modalId={signRequestDetachModalId} Component={ScanRequestDetachModalContent} />;
};

export default SignRequestDetachModal;
